import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MentoringTableMenuService } from '@app/mentoring/components/mentoring/services/mentoring-table-menu.service';
import { MentoringService } from '@app/mentoring/components/mentoring/services/mentoring.service';
import { Mentorship } from '@app/mentoring/mentoring-api.model';
import {
  MentorshipMenuOptions,
  MentorshipStatusEnum,
  MentorshipTypeEnum,
} from '@app/mentoring/mentoring.enums';

import { OpportunityFlagsService } from '@app/opportunities/services/opportunity-flags.service';
import { MenuSettings } from '@app/shared/components/data-table/data-table';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

@Component({
  selector: 'dgx-mentoring-mentees',
  templateUrl: './mentoring-mentees.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MentoringMenteesComponent implements OnInit {
  @Input() public isLoading = false;
  @Input() public isSorting = false;
  @Input() public mentees: Mentorship[] = [];

  @Output() public menteesUpdate = new EventEmitter<Mentorship[]>();

  @ViewChild('contactBy', { static: true })
  public contactTemplate: TemplateRef<HTMLElement>;
  @ViewChild('status', { static: true })
  public statusTemplate: TemplateRef<HTMLElement>;
  public i18n = this.translate.instant([
    'Core_Accept',
    'Core_Cancel',
    'Core_Complete',
    'Core_Details',
    'Core_Decline',
    'Core_Remove',
    'Mentoring_ReviewMentorship',
  ]);
  public menteeColumns = [];
  public menuSettings: MenuSettings;

  constructor(
    private cdr: ChangeDetectorRef,
    private opportunityFlagsService: OpportunityFlagsService,
    private mentoringService: MentoringService,
    private mentoringTableMenuService: MentoringTableMenuService,
    private translate: TranslateService
  ) {}

  public ngOnInit(): void {
    this.menteeColumns = this.mentoringService.getColumnConfig(
      MentorshipTypeEnum.Mentee,
      this.contactTemplate,
      this.statusTemplate
    );
    this.menuSettings = {
      placement: 'bottom-right',
      getMenuConfig: this.configureMenteeTableMenu,
    };
  }

  public configureMenteeTableMenu = (
    mentorship: Mentorship
  ): Observable<MenuViewModel[]> => {
    const status = mentorship.mentorshipStatusName;

    const menu: MenuViewModel[] = [
      {
        id: MentorshipMenuOptions.Accept,
        title: this.i18n.Core_Accept,
        isHidden: () => status !== MentorshipStatusEnum.Requested,
        defaultAction: (_, elementRef: ElementRef<HTMLElement>) =>
          this.menuAction(mentorship, MentorshipMenuOptions.Accept, elementRef),
        preventRefocus: true,
      },
      {
        id: MentorshipMenuOptions.Decline,
        title: this.i18n.Core_Decline,
        isHidden: () => status !== MentorshipStatusEnum.Requested,
        defaultAction: (_, elementRef: ElementRef<HTMLElement>) =>
          this.menuAction(
            mentorship,
            MentorshipMenuOptions.Decline,
            elementRef
          ),
        preventRefocus: true,
      },
      {
        id: MentorshipMenuOptions.Complete,
        title: this.i18n.Core_Complete,
        isHidden: () => status !== MentorshipStatusEnum.Accepted,
        defaultAction: (_, elementRef: ElementRef<HTMLElement>) =>
          this.menuAction(
            mentorship,
            MentorshipMenuOptions.Complete,
            elementRef
          ),
        preventRefocus: true,
      },
      {
        id: MentorshipMenuOptions.Review,
        title: this.i18n.Mentoring_ReviewMentorship,
        isHidden: () =>
          !(
            status === MentorshipStatusEnum.Completed &&
            this.opportunityFlagsService.useCompleteMentorshipEnhancements
          ),
        defaultAction: (_, elementRef: ElementRef<HTMLElement>) =>
          this.menuAction(mentorship, MentorshipMenuOptions.Review, elementRef),
        preventRefocus: true,
      },
      {
        id: MentorshipMenuOptions.Details,
        title: this.i18n.Core_Details,
        defaultAction: (_, elementRef: ElementRef<HTMLElement>) =>
          this.menuAction(
            mentorship,
            MentorshipMenuOptions.Details,
            elementRef
          ),
        preventRefocus: true,
      },
      {
        id: MentorshipMenuOptions.Remove,
        title: this.i18n.Core_Remove,
        isHidden: () =>
          !(
            status === MentorshipStatusEnum.Completed ||
            status === MentorshipStatusEnum.Declined
          ),
        defaultAction: (_, elementRef: ElementRef<HTMLElement>) =>
          this.menuAction(mentorship, MentorshipMenuOptions.Remove, elementRef),
        preventRefocus: true,
      },
    ];

    return of(menu);
  };

  private menuAction(
    mentorship: Mentorship,
    menuOption: MentorshipMenuOptions,
    elementRef: ElementRef<HTMLElement>
  ) {
    let observable: Observable<Mentorship[]>;
    this.isSorting = true;

    switch (menuOption) {
      case MentorshipMenuOptions.Accept:
        observable = this.mentoringTableMenuService.acceptMentorship(
          this.mentees,
          mentorship,
          MentorshipTypeEnum.Mentee,
          elementRef.nativeElement
        );
        break;
      case MentorshipMenuOptions.Decline:
        observable = this.mentoringTableMenuService.declineMentorship(
          this.mentees,
          mentorship,
          MentorshipTypeEnum.Mentee,
          elementRef.nativeElement
        );
        break;
      case MentorshipMenuOptions.Remove:
        observable = this.mentoringTableMenuService.removeMentorship(
          this.mentees,
          mentorship,
          MentorshipTypeEnum.Mentee,
          elementRef.nativeElement
        );
        break;
      case MentorshipMenuOptions.Details:
        observable = this.mentoringTableMenuService.moreDetailsMentorship(
          this.mentees,
          mentorship,
          MentorshipTypeEnum.Mentee,
          elementRef.nativeElement
        );
        break;
      case MentorshipMenuOptions.Review:
        observable = this.mentoringTableMenuService.reviewMentorship(
          this.mentees,
          mentorship,
          MentorshipTypeEnum.Mentee,
          elementRef.nativeElement
        );
        break;
      case MentorshipMenuOptions.Complete:
        if (this.opportunityFlagsService.useCompleteMentorshipEnhancements) {
          observable = this.mentoringTableMenuService.reviewMentorship(
            this.mentees,
            mentorship,
            MentorshipTypeEnum.Mentee,
            elementRef.nativeElement
          );
        } else {
          observable = this.mentoringTableMenuService.completeMentorship(
            this.mentees,
            mentorship,
            MentorshipTypeEnum.Mentee,
            elementRef.nativeElement
          );
        }
        break;
    }

    // tap off of observable and set mentees
    return observable
      .pipe(tap((mentees: Mentorship[]) => (this.mentees = mentees)))
      .pipe(
        finalize(() => {
          this.isSorting = false;
          this.menteesUpdate.emit(this.mentees);
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }
}
